import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { adminAPI } from '../../utils/api';
import { formatDateDDMMYYYY, formatDateWithWeekday } from '../../utils/dateFormat';
import { toast } from 'sonner';
import { 
  ArrowLeft, Clock, Plus, Trash2, Calendar, User, MapPin,
  RefreshCw, X, Check, AlertCircle, ChevronRight, UserX, Edit2, History
} from 'lucide-react';

const OvertimeManagement = () => {
  const navigate = useNavigate();
  const { userType, loading: authLoading } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [supervisors, setSupervisors] = useState([]);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('today'); // 'today', 'upcoming', 'past'
  
  // Today's OT
  const [todayAssignments, setTodayAssignments] = useState([]);
  const today = new Date().toISOString().split('T')[0];
  
  // Past OT
  const [pastDate, setPastDate] = useState('');
  const [pastAssignments, setPastAssignments] = useState([]);
  const [loadingPast, setLoadingPast] = useState(false);
  
  // Upcoming OT
  const [upcomingAssignments, setUpcomingAssignments] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);
  
  // Modal state
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [assigning, setAssigning] = useState(false);
  
  // Step 1: Select Date
  const [selectedDate, setSelectedDate] = useState('');
  
  // Step 2: Select Supervisor (whose parks to cover)
  const [selectedSupervisor, setSelectedSupervisor] = useState('');
  
  // Step 3: Select Parks
  const [supervisorParks, setSupervisorParks] = useState([]);
  const [loadingParks, setLoadingParks] = useState(false);
  const [selectedParks, setSelectedParks] = useState([]);
  
  // Step 4: Select OT Supervisor
  const [selectedOTSupervisor, setSelectedOTSupervisor] = useState('');
  
  // Step 5: Remarks
  const [remarks, setRemarks] = useState('');
  
  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editOTSupervisor, setEditOTSupervisor] = useState('');
  const [editRemarks, setEditRemarks] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Bulk action state
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedAssignments, setSelectedAssignments] = useState([]);
  const [showBulkReassignModal, setShowBulkReassignModal] = useState(false);
  const [bulkNewSupervisor, setBulkNewSupervisor] = useState('');
  const [bulkReassigning, setBulkReassigning] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Filter state for today's OT
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'pending', 'done'

  useEffect(() => {
    if (!authLoading && userType !== 'admin') {
      navigate('/admin/login');
    }
  }, [authLoading, userType, navigate]);

  useEffect(() => {
    fetchTodayData();
  }, []);

  const fetchTodayData = async () => {
    setLoading(true);
    setLoadingUpcoming(true);
    try {
      const [supResponse, otResponse, upcomingResponse] = await Promise.all([
        adminAPI.getSupervisors(),
        adminAPI.getOvertimeAssignments(null, today, null, false),
        adminAPI.getOvertimeAssignments(null, null, null, true)
      ]);
      
      setSupervisors(supResponse.data.inspectors || supResponse.data.supervisors || []);
      setTodayAssignments(otResponse.data.overtime_assignments || []);
      setUpcomingAssignments(upcomingResponse.data.overtime_assignments || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
      setLoadingUpcoming(false);
    }
  };

  const fetchPastData = async (date) => {
    if (!date) return;
    setLoadingPast(true);
    try {
      const response = await adminAPI.getOvertimeAssignments(null, date, null);
      setPastAssignments(response.data.overtime_assignments || []);
    } catch (error) {
      toast.error('Failed to load past data');
    } finally {
      setLoadingPast(false);
    }
  };

  const handlePastDateChange = (date) => {
    setPastDate(date);
    if (date) {
      fetchPastData(date);
    } else {
      setPastAssignments([]);
    }
  };

  // When supervisor changes in step 2, fetch their parks
  const handleSupervisorChange = async (supervisorId) => {
    setSelectedSupervisor(supervisorId);
    setSelectedParks([]);
    setSupervisorParks([]);
    
    if (!supervisorId || !selectedDate) return;
    
    setLoadingParks(true);
    try {
      const response = await adminAPI.getSupervisorParksForOT(supervisorId, selectedDate);
      setSupervisorParks(response.data.parks || []);
    } catch (error) {
      toast.error('Failed to load parks');
    } finally {
      setLoadingParks(false);
    }
  };

  // Toggle park selection
  const togglePark = (parkId) => {
    setSelectedParks(prev => 
      prev.includes(parkId) 
        ? prev.filter(p => p !== parkId)
        : [...prev, parkId]
    );
  };

  // Select all parks
  const selectAllParks = () => {
    const available = supervisorParks.filter(p => !p.already_assigned_ot).map(p => p.park_id);
    setSelectedParks(available);
  };

  // Handle assign overtime
  const handleAssignOvertime = async () => {
    if (!selectedDate || !selectedSupervisor || selectedParks.length === 0 || !selectedOTSupervisor) {
      toast.error('Please fill all required fields');
      return;
    }
    if (selectedSupervisor === selectedOTSupervisor) {
      toast.error('Absent supervisor and OT supervisor cannot be same');
      return;
    }
    
    setAssigning(true);
    try {
      await adminAPI.assignOvertime({
        absent_supervisor_id: selectedSupervisor,
        ot_supervisor_id: selectedOTSupervisor,
        park_ids: selectedParks,
        date: selectedDate,
        remarks: remarks.trim()
      });
      
      toast.success('Overtime assigned successfully');
      setShowAssignModal(false);
      resetForm();
      fetchTodayData();
      // Also refresh past data if viewing the same date
      if (pastDate === selectedDate) {
        fetchPastData(pastDate);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign overtime');
    } finally {
      setAssigning(false);
    }
  };

  const handleDeleteAssignment = async (assignmentId, isFromPast = false) => {
    if (!window.confirm('Delete this overtime assignment?')) return;
    
    try {
      await adminAPI.deleteOvertimeAssignment(assignmentId);
      toast.success('Assignment deleted');
      if (isFromPast) {
        fetchPastData(pastDate);
      } else {
        fetchTodayData();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openEditModal = (assignment) => {
    setEditingAssignment(assignment);
    setEditOTSupervisor(assignment.ot_supervisor_id || assignment.supervisor_id || '');
    setEditRemarks(assignment.remarks || '');
    setShowEditModal(true);
  };

  const handleSaveEdit = async (isFromPast = false) => {
    if (!editingAssignment) return;
    
    setSaving(true);
    try {
      await adminAPI.updateOvertimeAssignment(editingAssignment.id, {
        ot_supervisor_id: editOTSupervisor,
        remarks: editRemarks.trim()
      });
      toast.success('Assignment updated');
      setShowEditModal(false);
      setEditingAssignment(null);
      if (isFromPast) {
        fetchPastData(pastDate);
      } else {
        fetchTodayData();
      }
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setCurrentStep(1);
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setSelectedSupervisor('');
    setSupervisorParks([]);
    setSelectedParks([]);
    setSelectedOTSupervisor('');
    setRemarks('');
  };

  const openAssignModal = () => {
    resetForm();
    setShowAssignModal(true);
  };

  // Bulk reassign functions
  const toggleBulkSelect = () => {
    if (bulkSelectMode) {
      setSelectedAssignments([]);
    }
    setBulkSelectMode(!bulkSelectMode);
  };

  const toggleAssignmentSelection = (assignmentId) => {
    setSelectedAssignments(prev => 
      prev.includes(assignmentId)
        ? prev.filter(id => id !== assignmentId)
        : [...prev, assignmentId]
    );
  };

  const selectAllAssignmentsForDate = (dateAssignments) => {
    // Only select pending (not completed) assignments
    const pendingAssignments = dateAssignments.filter(a => !a.is_completed);
    const ids = pendingAssignments.map(a => a.id);
    const allSelected = ids.length > 0 && ids.every(id => selectedAssignments.includes(id));
    if (allSelected) {
      setSelectedAssignments(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedAssignments(prev => [...new Set([...prev, ...ids])]);
    }
  };

  const openBulkReassignModal = () => {
    setBulkNewSupervisor('');
    setShowBulkReassignModal(true);
  };

  const handleBulkReassign = async () => {
    if (!bulkNewSupervisor || selectedAssignments.length === 0) {
      toast.error('Select a supervisor');
      return;
    }

    setBulkReassigning(true);
    try {
      // Update each assignment
      let successCount = 0;
      let failCount = 0;
      
      for (const assignmentId of selectedAssignments) {
        try {
          await adminAPI.updateOvertimeAssignment(assignmentId, {
            ot_supervisor_id: bulkNewSupervisor,
            remarks: ''
          });
          successCount++;
        } catch {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} assignment(s) reassigned successfully`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} assignment(s) failed to update`);
      }
      
      setShowBulkReassignModal(false);
      setSelectedAssignments([]);
      setBulkSelectMode(false);
      fetchTodayData();
    } catch (error) {
      toast.error('Failed to reassign');
    } finally {
      setBulkReassigning(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAssignments.length === 0) return;
    
    if (!window.confirm(`Delete ${selectedAssignments.length} OT assignment(s)? This cannot be undone.`)) {
      return;
    }

    setBulkDeleting(true);
    try {
      let successCount = 0;
      let failCount = 0;
      
      for (const assignmentId of selectedAssignments) {
        try {
          await adminAPI.deleteOvertimeAssignment(assignmentId);
          successCount++;
        } catch {
          failCount++;
        }
      }
      
      if (successCount > 0) {
        toast.success(`${successCount} assignment(s) deleted`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} assignment(s) failed to delete`);
      }
      
      setSelectedAssignments([]);
      setBulkSelectMode(false);
      fetchTodayData();
    } catch (error) {
      toast.error('Failed to delete');
    } finally {
      setBulkDeleting(false);
    }
  };

  // Get current tab assignments
  const getCurrentAssignments = () => {
    switch (activeTab) {
      case 'today': return todayAssignments;
      case 'upcoming': return upcomingAssignments;
      case 'past': return pastAssignments;
      default: return [];
    }
  };

  const getCurrentPending = () => {
    const assignments = getCurrentAssignments();
    return assignments.filter(a => !a.is_completed).length;
  };

  // Get supervisor name by ID - check if stored name is actually an ID
  const getSupervisorName = (id) => {
    const sup = supervisors.find(s => s.supervisor_id === id);
    return sup ? (sup.supervisor_name || sup.name || id) : id;
  };

  // Get display name - use stored name if it's a real name, otherwise look up from list
  const getDisplayName = (storedName, id) => {
    // If stored name looks like an ID (starts with ITP or similar pattern), look it up
    if (!storedName || storedName === id || /^[A-Z]{2,3}\d{2,4}$/i.test(storedName)) {
      return getSupervisorName(id);
    }
    return storedName;
  };

  // Get pending count for today
  const todayPending = todayAssignments.filter(a => !a.is_completed).length;
  const todayCompleted = todayAssignments.filter(a => a.is_completed).length;
  
  // Filtered today assignments based on status filter
  const filteredTodayAssignments = statusFilter === 'all' 
    ? todayAssignments 
    : statusFilter === 'pending'
      ? todayAssignments.filter(a => !a.is_completed)
      : todayAssignments.filter(a => a.is_completed);

  // Navigation validation
  const canProceedToStep2 = selectedDate;
  const canProceedToStep3 = selectedSupervisor && supervisorParks.length > 0;
  const canProceedToStep4 = selectedParks.length > 0;
  const canProceedToStep5 = selectedOTSupervisor && selectedOTSupervisor !== selectedSupervisor;

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent"></div>
      </div>
    );
  }

  // Render assignment card (reusable for today and past)
  const renderAssignmentCard = (a, isFromPast = false) => (
    <div 
      key={a.id} 
      className={`p-4 rounded-lg border-2 ${
        a.is_completed 
          ? 'bg-green-50 border-green-200' 
          : 'bg-white border-slate-200'
      } ${bulkSelectMode && selectedAssignments.includes(a.id) ? 'ring-2 ring-red-500' : ''}`}
    >
      <div className="flex items-start gap-3">
        {bulkSelectMode && !isFromPast && (
          <input
            type="checkbox"
            checked={selectedAssignments.includes(a.id)}
            onChange={() => !a.is_completed && toggleAssignmentSelection(a.id)}
            disabled={a.is_completed}
            className="w-5 h-5 rounded border-slate-300 mt-1 disabled:opacity-50"
          />
        )}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-600" />
              <span className="font-bold">{a.park_name}</span>
              <span className="text-xs bg-slate-100 px-2 py-0.5 rounded">Zone {a.zone}</span>
            </div>
            {a.is_completed ? (
              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                ✓ Done
              </span>
            ) : (
              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 text-xs font-bold rounded-full">
                Pending
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="bg-red-50 rounded p-2">
              <div className="flex items-center gap-1 text-xs text-red-500 mb-1">
                <UserX className="w-3 h-3" />
                <span>Absent Supervisor</span>
              </div>
              <div className="font-bold text-red-700">{getDisplayName(a.absent_supervisor_name, a.supervisor_id || a.absent_supervisor_id)}</div>
              <div className="text-xs text-red-500">{a.supervisor_id || a.absent_supervisor_id}</div>
            </div>
            <div className="bg-green-50 rounded p-2">
              <div className="flex items-center gap-1 text-xs text-green-500 mb-1">
                <User className="w-3 h-3" />
                <span>OT Supervisor</span>
              </div>
              <div className="font-bold text-green-700">{getDisplayName(a.ot_supervisor_name, a.ot_supervisor_id)}</div>
              <div className="text-xs text-green-500">{a.ot_supervisor_id}</div>
            </div>
          </div>
          {a.remarks && (
            <div className="text-xs text-slate-500 mt-2">Remarks: {a.remarks}</div>
          )}
        </div>
        {!bulkSelectMode && (
          <div className="flex flex-col gap-1">
            <button
              onClick={() => openEditModal(a)}
              className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
              title="Edit"
            >
              <Edit2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDeleteAssignment(a.id, isFromPast)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-stone-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-700 to-amber-800 text-white p-4 sticky top-0 z-50 shadow-lg">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 hover:bg-white/20 rounded-lg">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Clock className="w-6 h-6" />
                Overtime Management
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openAssignModal}
              className="px-4 py-2 bg-white text-amber-700 rounded-lg font-bold flex items-center gap-2 hover:bg-amber-50"
              data-testid="assign-overtime-btn"
            >
              <Plus className="w-5 h-5" />
              Assign OT
            </button>
          </div>
        </div>
        
        {/* Tab Navigation */}
        <div className="flex gap-2 mt-4 max-w-7xl mx-auto">
          <button
            onClick={() => { setActiveTab('today'); setBulkSelectMode(false); setSelectedAssignments([]); }}
            className={`px-4 py-2 rounded-t-lg font-bold transition-colors flex items-center gap-2 ${
              activeTab === 'today' 
                ? 'bg-white text-amber-700' 
                : 'bg-amber-600 text-white hover:bg-amber-500'
            }`}
          >
            <Clock className="w-4 h-4" />
            Today
            {todayAssignments.length > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === 'today' ? 'bg-amber-100 text-amber-700' : 'bg-amber-500'}`}>
                {todayAssignments.length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('upcoming'); setBulkSelectMode(false); setSelectedAssignments([]); }}
            className={`px-4 py-2 rounded-t-lg font-bold transition-colors flex items-center gap-2 ${
              activeTab === 'upcoming' 
                ? 'bg-white text-amber-700' 
                : 'bg-amber-600 text-white hover:bg-amber-500'
            }`}
          >
            <Calendar className="w-4 h-4" />
            Upcoming
            {upcomingAssignments.length > 0 && (
              <span className={`px-2 py-0.5 rounded-full text-xs ${activeTab === 'upcoming' ? 'bg-blue-100 text-blue-700' : 'bg-blue-500'}`}>
                {upcomingAssignments.length}
              </span>
            )}
          </button>
          <button
            onClick={() => { setActiveTab('past'); setBulkSelectMode(false); setSelectedAssignments([]); }}
            className={`px-4 py-2 rounded-t-lg font-bold transition-colors flex items-center gap-2 ${
              activeTab === 'past' 
                ? 'bg-white text-amber-700' 
                : 'bg-amber-600 text-white hover:bg-amber-500'
            }`}
          >
            <History className="w-4 h-4" />
            Past
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Bulk Action Bar */}
        {bulkSelectMode && (
          <div className="bg-amber-100 border-2 border-amber-300 rounded-lg p-4 mb-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={getCurrentPending() > 0 && selectedAssignments.length === getCurrentPending()}
                onChange={() => {
                  const pendingIds = getCurrentAssignments().filter(a => !a.is_completed).map(a => a.id);
                  if (selectedAssignments.length === pendingIds.length) {
                    setSelectedAssignments([]);
                  } else {
                    setSelectedAssignments(pendingIds);
                  }
                }}
                className="w-5 h-5 rounded"
              />
              <span className="font-bold text-amber-800">
                {selectedAssignments.length} selected
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={openBulkReassignModal}
                disabled={selectedAssignments.length === 0 || bulkReassigning}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${bulkReassigning ? 'animate-spin' : ''}`} />
                Reassign
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={selectedAssignments.length === 0 || bulkDeleting}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2"
              >
                <Trash2 className={`w-4 h-4 ${bulkDeleting ? 'animate-spin' : ''}`} />
                Delete
              </button>
              <button
                onClick={() => { setBulkSelectMode(false); setSelectedAssignments([]); }}
                className="px-4 py-2 bg-slate-500 hover:bg-slate-600 text-white rounded-lg font-bold"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Today's OT Tab */}
        {activeTab === 'today' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-amber-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6" />
                <h2 className="text-lg font-bold">
                  Today's Overtime - {formatDateDDMMYYYY(new Date().toISOString().split('T')[0])}
                </h2>
              </div>
              <div className="flex items-center gap-3">
                {todayAssignments.length > 0 && (
                  <>
                    <button
                      onClick={() => setStatusFilter(statusFilter === 'pending' ? 'all' : 'pending')}
                      className={`px-3 py-1 rounded-full text-sm font-bold transition-all ${
                        statusFilter === 'pending' ? 'bg-yellow-400 text-yellow-900 ring-2 ring-white' : 'bg-yellow-400 text-yellow-900'
                      }`}
                    >
                      {todayPending} Pending
                    </button>
                    <button
                      onClick={() => setStatusFilter(statusFilter === 'done' ? 'all' : 'done')}
                      className={`px-3 py-1 rounded-full text-sm font-bold transition-all ${
                        statusFilter === 'done' ? 'bg-green-400 text-green-900 ring-2 ring-white' : 'bg-green-400 text-green-900'
                      }`}
                    >
                      {todayCompleted} Done
                    </button>
                    {todayPending > 0 && (
                      <button
                        onClick={() => setBulkSelectMode(!bulkSelectMode)}
                        className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-full text-sm font-bold"
                      >
                        Bulk Actions
                      </button>
                    )}
                  </>
                )}
                <button onClick={fetchTodayData} disabled={loading} className="p-2 hover:bg-white/20 rounded-lg">
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            <div className="p-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-amber-600 border-t-transparent"></div>
                </div>
              ) : todayAssignments.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-semibold">No overtime assignments for today</p>
                  <p className="text-sm">Click "Assign OT" to create one</p>
                </div>
              ) : filteredTodayAssignments.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-semibold">No {statusFilter} assignments</p>
                  <button onClick={() => setStatusFilter('all')} className="mt-2 text-amber-600 hover:underline">Show all</button>
                </div>
              ) : (
                <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                  {filteredTodayAssignments.map(a => renderAssignmentCard(a, false))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upcoming OT Tab */}
        {activeTab === 'upcoming' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-blue-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6" />
                <h2 className="text-lg font-bold">Upcoming Overtime</h2>
              </div>
              <div className="flex items-center gap-3">
                {upcomingAssignments.length > 0 && (
                  <>
                    <span className="bg-blue-400 px-3 py-1 rounded-full text-sm font-bold">
                      {upcomingAssignments.length} scheduled
                    </span>
                    <button
                      onClick={() => setBulkSelectMode(!bulkSelectMode)}
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-full text-sm font-bold"
                    >
                      Bulk Actions
                    </button>
                  </>
                )}
                <button onClick={fetchTodayData} disabled={loadingUpcoming} className="p-2 hover:bg-white/20 rounded-lg">
                  <RefreshCw className={`w-5 h-5 ${loadingUpcoming ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
            <div className="p-4">
              {loadingUpcoming ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
                </div>
              ) : upcomingAssignments.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-semibold">No upcoming overtime scheduled</p>
                  <p className="text-sm">Assign OT for future dates to see them here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {Object.entries(
                    upcomingAssignments.reduce((acc, a) => {
                      if (!acc[a.date]) acc[a.date] = [];
                      acc[a.date].push(a);
                      return acc;
                    }, {})
                  ).sort(([a], [b]) => a.localeCompare(b)).map(([date, assignments]) => (
                    <div key={date} className="border rounded-lg overflow-hidden">
                      <div className="bg-blue-50 px-4 py-2 flex items-center justify-between">
                        <span className="font-bold text-blue-800">
                          {formatDateWithWeekday(date)}
                        </span>
                        <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-sm font-bold">
                          {assignments.length} park(s)
                        </span>
                      </div>
                      <div className="p-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                        {assignments.map(a => renderAssignmentCard(a, false))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Past OT Tab */}
        {activeTab === 'past' && (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="bg-slate-600 text-white p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <History className="w-6 h-6" />
                <h2 className="text-lg font-bold">Past Overtime</h2>
              </div>
              <div className="flex items-center gap-4">
                <input
                  type="date"
                  value={pastDate}
                  onChange={(e) => handlePastDateChange(e.target.value)}
                  max={new Date(Date.now() - 86400000).toISOString().split('T')[0]}
                  className="h-10 px-3 rounded-lg border-0 text-slate-800"
                />
                {pastAssignments.length > 0 && (
                  <button
                    onClick={() => setBulkSelectMode(!bulkSelectMode)}
                    className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded-full text-sm font-bold"
                  >
                    Bulk Actions
                  </button>
                )}
              </div>
            </div>
            <div className="p-4">
              {!pastDate ? (
                <div className="text-center py-12 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-semibold">Select a date to view past overtime</p>
                </div>
              ) : loadingPast ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-600 border-t-transparent"></div>
                </div>
              ) : pastAssignments.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-semibold">No overtime for {formatDateDDMMYYYY(pastDate)}</p>
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-700">
                      {formatDateWithWeekday(pastDate)}
                    </h3>
                    <div className="flex gap-2">
                      <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-sm font-bold">
                        {pastAssignments.filter(a => !a.is_completed).length} Pending
                      </span>
                      <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm font-bold">
                        {pastAssignments.filter(a => a.is_completed).length} Done
                      </span>
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                    {pastAssignments.map(a => renderAssignmentCard(a, true))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Bulk Reassign Modal */}
      {showBulkReassignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
            <div className="bg-green-600 text-white p-4 flex items-center justify-between rounded-t-xl">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <RefreshCw className="w-5 h-5" />
                Bulk Reassign
              </h2>
              <button onClick={() => setShowBulkReassignModal(false)} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                <div className="font-bold text-orange-800">
                  {selectedAssignments.length} park(s) selected
                </div>
                <div className="text-sm text-orange-600 mt-1">
                  All selected parks will be reassigned to the new supervisor
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Reassign All To: *
                </label>
                <select
                  value={bulkNewSupervisor}
                  onChange={(e) => setBulkNewSupervisor(e.target.value)}
                  className="w-full h-12 px-4 border-2 border-slate-300 rounded-lg bg-white focus:border-green-600"
                  data-testid="bulk-supervisor-select"
                >
                  <option value="">-- Select New Supervisor --</option>
                  {supervisors.map(s => (
                    <option key={s.supervisor_id} value={s.supervisor_id}>
                      {s.supervisor_name || s.name} ({s.supervisor_id})
                    </option>
                  ))}
                </select>
              </div>
              
              {bulkNewSupervisor && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-800">
                      New OT Supervisor: {getSupervisorName(bulkNewSupervisor)}
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowBulkReassignModal(false)}
                className="px-4 py-2 border-2 border-slate-300 rounded-lg font-bold hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkReassign}
                disabled={bulkReassigning || !bulkNewSupervisor}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2"
                data-testid="confirm-bulk-reassign-btn"
              >
                {bulkReassigning ? (
                  <>
                    <RefreshCw className="w-5 h-5 animate-spin" />
                    Reassigning...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    Confirm Reassign
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Overtime Modal */}
      {showEditModal && editingAssignment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
            <div className="bg-blue-600 text-white p-4 flex items-center justify-between rounded-t-xl">
              <h2 className="text-lg font-bold">Edit Overtime Assignment</h2>
              <button onClick={() => setShowEditModal(false)} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-sm text-slate-500">Date</div>
                <div className="font-bold">{editingAssignment.date}</div>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-sm text-slate-500">Park</div>
                <div className="font-bold">{editingAssignment.park_name}</div>
              </div>
              
              <div className="bg-red-50 rounded-lg p-3">
                <div className="text-sm text-red-500">Absent Supervisor</div>
                <div className="font-bold text-red-700">{editingAssignment.absent_supervisor_name}</div>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">OT Supervisor *</label>
                <select
                  value={editOTSupervisor}
                  onChange={(e) => setEditOTSupervisor(e.target.value)}
                  className="w-full h-12 px-4 border-2 border-slate-300 rounded-lg bg-white"
                >
                  <option value="">-- Select --</option>
                  {supervisors.map(s => (
                    <option key={s.supervisor_id} value={s.supervisor_id}>
                      {s.supervisor_name || s.name} ({s.supervisor_id})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">Remarks</label>
                <textarea
                  value={editRemarks}
                  onChange={(e) => setEditRemarks(e.target.value)}
                  className="w-full h-20 px-4 py-3 border-2 border-slate-300 rounded-lg resize-none"
                />
              </div>
            </div>
            
            <div className="p-4 border-t flex justify-end gap-3">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 border-2 border-slate-300 rounded-lg font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving || !editOTSupervisor}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg font-bold"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Overtime Modal - 5 Step Wizard */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-xl">
            {/* Modal Header */}
            <div className="bg-orange-600 text-white p-4 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-bold flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Assign Overtime
                </h2>
                <p className="text-sm opacity-90">Step {currentStep} of 5</p>
              </div>
              <button onClick={() => setShowAssignModal(false)} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Progress Indicator */}
            <div className="bg-orange-50 px-4 py-2 flex items-center gap-1 text-xs">
              <span className={`px-2 py-1 rounded ${currentStep >= 1 ? 'bg-orange-600 text-white' : 'bg-slate-200'}`}>1. Date</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className={`px-2 py-1 rounded ${currentStep >= 2 ? 'bg-orange-600 text-white' : 'bg-slate-200'}`}>2. Supervisor</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className={`px-2 py-1 rounded ${currentStep >= 3 ? 'bg-orange-600 text-white' : 'bg-slate-200'}`}>3. Parks</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className={`px-2 py-1 rounded ${currentStep >= 4 ? 'bg-orange-600 text-white' : 'bg-slate-200'}`}>4. OT Sup</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className={`px-2 py-1 rounded ${currentStep >= 5 ? 'bg-orange-600 text-white' : 'bg-slate-200'}`}>5. Confirm</span>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {/* Step 1: Select Date */}
              {currentStep === 1 && (
                <div>
                  <h3 className="font-bold text-slate-700 mb-4">Select Date for Overtime</h3>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full h-12 px-4 border-2 border-slate-300 rounded-lg focus:border-orange-600"
                  />
                  {selectedDate && (
                    <div className="mt-4 bg-green-50 border-2 border-green-200 rounded-lg p-4 text-green-700">
                      <Check className="w-5 h-5 inline mr-2" />
                      Date selected: {formatDateWithWeekday(selectedDate)}
                    </div>
                  )}
                </div>
              )}

              {/* Step 2: Select Supervisor (whose parks to cover) */}
              {currentStep === 2 && (
                <div>
                  <h3 className="font-bold text-slate-700 mb-4">Select Supervisor (whose parks to cover)</h3>
                  <select
                    value={selectedSupervisor}
                    onChange={(e) => handleSupervisorChange(e.target.value)}
                    className="w-full h-12 px-4 border-2 border-slate-300 rounded-lg bg-white focus:border-orange-600"
                  >
                    <option value="">-- Select Supervisor --</option>
                    {supervisors.map(s => (
                      <option key={s.supervisor_id} value={s.supervisor_id}>
                        {s.supervisor_name || s.name} ({s.supervisor_id})
                      </option>
                    ))}
                  </select>
                  
                  {loadingParks && (
                    <div className="mt-4 flex items-center gap-2 text-slate-500">
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Loading parks...
                    </div>
                  )}
                  
                  {selectedSupervisor && !loadingParks && supervisorParks.length === 0 && (
                    <div className="mt-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4 text-yellow-700">
                      <AlertCircle className="w-5 h-5 inline mr-2" />
                      No parks assigned to this supervisor
                    </div>
                  )}
                  
                  {selectedSupervisor && !loadingParks && supervisorParks.length > 0 && (
                    <div className="mt-4 bg-green-50 border-2 border-green-200 rounded-lg p-4 text-green-700">
                      <Check className="w-5 h-5 inline mr-2" />
                      {supervisorParks.length} parks found. Click Next to select parks.
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Select Parks */}
              {currentStep === 3 && (
                <div>
                  <h3 className="font-bold text-slate-700 mb-2">Select Parks to Cover</h3>
                  <p className="text-sm text-slate-500 mb-4">
                    Parks assigned to {getSupervisorName(selectedSupervisor)}
                    {selectedParks.length > 0 && <span className="text-orange-600 font-bold ml-2">({selectedParks.length} selected)</span>}
                  </p>
                  
                  <button
                    type="button"
                    onClick={selectAllParks}
                    className="text-sm text-orange-600 font-semibold mb-3"
                  >
                    Select All Available
                  </button>
                  
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {supervisorParks.map(p => (
                      <label
                        key={p.park_id}
                        className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${
                          p.already_assigned_ot 
                            ? 'border-slate-200 bg-slate-100 opacity-50 cursor-not-allowed' 
                            : selectedParks.includes(p.park_id)
                            ? 'border-green-600 bg-green-50'
                            : 'border-slate-200 hover:border-green-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedParks.includes(p.park_id)}
                          onChange={() => !p.already_assigned_ot && togglePark(p.park_id)}
                          disabled={p.already_assigned_ot}
                          className="w-5 h-5 rounded border-slate-300"
                        />
                        <div className="flex-1">
                          <div className="font-semibold">{p.park_name}</div>
                          <div className="text-sm text-slate-500">Zone {p.zone} | {p.manpower_count} workers</div>
                        </div>
                        {p.already_assigned_ot && (
                          <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Already Assigned</span>
                        )}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Step 4: Select OT Supervisor */}
              {currentStep === 4 && (
                <div>
                  <h3 className="font-bold text-slate-700 mb-4">Select Supervisor for Overtime</h3>
                  <p className="text-sm text-slate-500 mb-4">Who will cover {selectedParks.length} park(s)?</p>
                  
                  <select
                    value={selectedOTSupervisor}
                    onChange={(e) => setSelectedOTSupervisor(e.target.value)}
                    className="w-full h-12 px-4 border-2 border-slate-300 rounded-lg bg-white focus:border-green-600"
                  >
                    <option value="">-- Select OT Supervisor --</option>
                    {supervisors.map(s => (
                      <option key={s.supervisor_id} value={s.supervisor_id}>
                        {s.supervisor_name || s.name} ({s.supervisor_id})
                      </option>
                    ))}
                  </select>
                  
                  {selectedOTSupervisor && selectedOTSupervisor === selectedSupervisor && (
                    <div className="mt-4 bg-red-50 border-2 border-red-200 rounded-lg p-4 text-red-700">
                      <AlertCircle className="w-5 h-5 inline mr-2" />
                      OT supervisor cannot be same as absent supervisor
                    </div>
                  )}
                  
                  {selectedOTSupervisor && selectedOTSupervisor !== selectedSupervisor && (
                    <div className="mt-4 bg-green-50 border-2 border-green-200 rounded-lg p-4 text-green-700">
                      <Check className="w-5 h-5 inline mr-2" />
                      Selected: {getSupervisorName(selectedOTSupervisor)}
                    </div>
                  )}
                </div>
              )}

              {/* Step 5: Confirm */}
              {currentStep === 5 && (
                <div>
                  <h3 className="font-bold text-slate-700 mb-4">Confirm Overtime Assignment</h3>
                  
                  <div className="space-y-4">
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="text-sm text-slate-500">Date</div>
                      <div className="font-bold text-lg">
                        {formatDateWithWeekday(selectedDate)}
                      </div>
                    </div>
                    
                    <div className="bg-red-50 rounded-lg p-4">
                      <div className="text-sm text-red-500">Absent Supervisor</div>
                      <div className="font-bold text-lg text-red-700">{getSupervisorName(selectedSupervisor)}</div>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm text-green-500">OT Supervisor</div>
                      <div className="font-bold text-lg text-green-700">{getSupervisorName(selectedOTSupervisor)}</div>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-4">
                      <div className="text-sm text-blue-500">Parks ({selectedParks.length})</div>
                      <div className="mt-2 space-y-1">
                        {selectedParks.map(parkId => {
                          const park = supervisorParks.find(p => p.park_id === parkId);
                          return (
                            <div key={parkId} className="text-sm font-medium">
                              {park?.park_name} (Zone {park?.zone})
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-bold text-slate-700 mb-2">Remarks (Optional)</label>
                      <textarea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder="Enter any remarks..."
                        className="w-full h-20 px-4 py-3 border-2 border-slate-300 rounded-lg resize-none"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 border-t bg-slate-50 flex items-center justify-between">
              <button
                onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : setShowAssignModal(false)}
                className="px-6 py-2 border-2 border-slate-300 rounded-lg font-bold hover:bg-slate-100"
              >
                {currentStep > 1 ? 'Back' : 'Cancel'}
              </button>
              
              {currentStep < 5 ? (
                <button
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={
                    (currentStep === 1 && !canProceedToStep2) ||
                    (currentStep === 2 && !canProceedToStep3) ||
                    (currentStep === 3 && !canProceedToStep4) ||
                    (currentStep === 4 && !canProceedToStep5)
                  }
                  className="px-6 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 text-white rounded-lg font-bold flex items-center gap-2"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleAssignOvertime}
                  disabled={assigning}
                  className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-slate-300 text-white rounded-lg font-bold"
                >
                  {assigning ? 'Assigning...' : 'Confirm & Assign'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OvertimeManagement;
